import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { BASE_URL } from "@/config/api";
import { getAccessToken, getOrganizationId } from "@/utils/token_functions";
import { secureAxios } from "@/middleware/encryptionMiddleware";

export const fetchSuggestionsAsync = createAsyncThunk(
  "search/fetchSuggestions",
  async (query, { rejectWithValue, signal }) => {
    try {
      const token = getAccessToken();
      if (!token) throw new Error("No authentication token found");

      const url = `${BASE_URL}/v1/search/suggestions?q=${encodeURIComponent(query)}&limit=10`;

      const response = await secureAxios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        signal,
      });

      return response.data?.data || [];
    } catch (error) {
      if (error.name === "CanceledError" || error.name === "AbortError") {
        return rejectWithValue("cancelled");
      }
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const performFullSearchAsync = createAsyncThunk(
  "search/performFullSearch",
  async ({ query, organizationId, skip = 0, limit = 20 }, { rejectWithValue }) => {
    try {
      const token = getAccessToken();
      if (!token) throw new Error("No authentication token found");

      const orgId = organizationId || getOrganizationId();
      let url = `${BASE_URL}/v1/search?q=${encodeURIComponent(query)}&limit=${limit}&skip=${skip}`;
      if (orgId) url += `&organizationID=${orgId}`;

      const response = await secureAxios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      return response.data?.data || null;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

const searchSlice = createSlice({
  name: "search",
  initialState: {
    // The current text in the global search bar.
    query: "",
    // Typeahead suggestion results from /v1/search/suggestions.
    suggestions: [],
    loadingSuggestions: false,
    // Full search results from /v1/search (used on the SearchResults page).
    fullResults: null,
    loadingFull: false,
    error: null,
  },
  reducers: {
    setQuery: (state, action) => {
      state.query = action.payload;
    },
    clearSearch: (state) => {
      state.query = "";
      state.suggestions = [];
      state.fullResults = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Suggestions
      .addCase(fetchSuggestionsAsync.pending, (state) => {
        state.loadingSuggestions = true;
        state.error = null;
      })
      .addCase(fetchSuggestionsAsync.fulfilled, (state, action) => {
        state.loadingSuggestions = false;
        state.suggestions = action.payload;
      })
      .addCase(fetchSuggestionsAsync.rejected, (state, action) => {
        state.loadingSuggestions = false;
        // Don't surface cancelled requests as errors in the UI.
        if (action.payload !== "cancelled") {
          state.error = action.payload;
        }
      })
      // Full search
      .addCase(performFullSearchAsync.pending, (state) => {
        state.loadingFull = true;
        state.error = null;
      })
      .addCase(performFullSearchAsync.fulfilled, (state, action) => {
        state.loadingFull = false;
        state.fullResults = action.payload;
      })
      .addCase(performFullSearchAsync.rejected, (state, action) => {
        state.loadingFull = false;
        state.error = action.payload;
      });
  },
});

export const { setQuery, clearSearch } = searchSlice.actions;

export const selectSearchQuery = (state) => state.search.query;
export const selectSuggestions = (state) => state.search.suggestions;
export const selectLoadingSuggestions = (state) => state.search.loadingSuggestions;
export const selectFullResults = (state) => state.search.fullResults;
export const selectLoadingFull = (state) => state.search.loadingFull;
export const selectSearchError = (state) => state.search.error;

export const selectSearchTerm = () => "";

export default searchSlice.reducer;
